import { applyMongoFilter } from "@tellescope/utils";
import { extractRequestFromQueryKey } from "../../../keys";
import {
  convertChatMessageToInboxThread,
  dedupeInboxThreads,
  removeChatExcludedInboxThreadFields,
  threadIsNewerThanOldest,
  withNestedInfiniteDataList,
} from "../../../utils";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

export const CHATS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"chats"> = {
  createOne: (_, response) => {
    if (!response) {
      return {};
    }

    const threadFromResponse = convertChatMessageToInboxThread(response);

    return {
      _otherModels: {
        inbox_threads: {
          load_threads: {
            predicate: (query) => {
              if (!threadIsNewerThanOldest(query.state.data, threadFromResponse)) {
                return false;
              }

              const request = extractRequestFromQueryKey(query.queryKey);
              const filteredMdbFilter = removeChatExcludedInboxThreadFields(request?.mdbFilter ?? {});
              return applyMongoFilter(threadFromResponse, filteredMdbFilter);
            },
            request: {
              autobuild: true,
            },
            updater: (oldData) => {
              const threadId = response.roomId;
              const threadExists = oldData?.pages.some((page) =>
                page.threads.some((thread) => thread.threadId === threadId)
              );
              if (!threadExists) return oldData;
              return withNestedInfiniteDataList({
                infiniteData: oldData,
                listKey: "threads",
                operation: {
                  type: "upsert",
                  args: {
                    matchFn: (thread) => thread.threadId === threadId,
                    updater: (thread) =>
                      thread
                        ? {
                            ...thread,
                            preview: threadFromResponse.preview || thread.preview,
                            outboundPreview: threadFromResponse.outboundPreview || thread.outboundPreview,
                            timestamp: threadFromResponse.timestamp,
                            outboundTimestamp: threadFromResponse.outboundTimestamp ?? thread.outboundTimestamp,
                            recentOutboundUserId:
                              threadFromResponse.recentOutboundUserId ?? thread.recentOutboundUserId,
                            recentInboundEnduserId:
                              threadFromResponse.recentInboundEnduserId ?? thread.recentInboundEnduserId,
                          }
                        : undefined,
                    sortFn: (a, b) => {
                      const aTime = Math.max(
                        a.timestamp.getTime(),
                        a.outboundTimestamp instanceof Date ? a.outboundTimestamp.getTime() : 0
                      );
                      const bTime = Math.max(
                        b.timestamp.getTime(),
                        b.outboundTimestamp instanceof Date ? b.outboundTimestamp.getTime() : 0
                      );
                      return bTime - aTime;
                    },
                  },
                },
              });
            },
            exact: false,
          },
        },
      },
    };
  },
  createSome: (_, response) => {
    if (!response) {
      return {};
    }

    const threadsFromResponse = dedupeInboxThreads(response.created.map(convertChatMessageToInboxThread));

    return {
      _otherModels: {
        inbox_threads: {
          load_threads: {
            predicate: (query) => {
              if (threadsFromResponse.every((thread) => !threadIsNewerThanOldest(query.state.data, thread))) {
                return false;
              }

              const request = extractRequestFromQueryKey(query.queryKey);
              const filteredMdbFilter = removeChatExcludedInboxThreadFields(request?.mdbFilter ?? {});
              return threadsFromResponse.some((thread) => applyMongoFilter(thread, filteredMdbFilter));
            },
            request: {
              autobuild: true,
            },
            exact: false,
          },
        },
      },
    };
  },
  updateOne: (_, response) => {
    if (!response) {
      return {};
    }

    const threadFromResponse = convertChatMessageToInboxThread(response);

    return {
      _otherModels: {
        inbox_threads: {
          load_threads: {
            predicate: (query) => {
              // Check if message is already in preview first - if so, always invalidate
              const isInPreview = query.state.data?.pages.some((page) =>
                page.threads.some((thread) => thread.type === "Chat" && thread.threadId === response.id)
              );
              if (isInPreview) {
                return true;
              }

              // If not in preview, check if it's newer than oldest cached thread
              if (!threadIsNewerThanOldest(query.state.data, threadFromResponse)) {
                return false;
              }

              // Finally, check if it matches the filter
              const queryRequest = extractRequestFromQueryKey(query.queryKey);
              const filteredMdbFilter = removeChatExcludedInboxThreadFields(queryRequest?.mdbFilter ?? {});
              return applyMongoFilter(threadFromResponse, filteredMdbFilter);
            },
            request: {
              autobuild: true,
            },
            exact: false,
          },
        },
      },
    };
  },
  deleteOne: (messageIdFromRequest) => ({
    _otherModels: {
      inbox_threads: {
        load_threads: {
          predicate: (query) => {
            const isInPreview = query.state.data?.pages.some((page) =>
              page.threads.some((thread) => thread.type === "Chat" && thread.threadId === messageIdFromRequest)
            );

            return !!isInPreview;
          },
          request: {
            autobuild: true,
          },
          exact: false,
        },
      },
    },
  }),
};
